import "./InfoPanel.css";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";
import useOverlay from "../../utils/useOverlay";
import AboutTab, { LANGUAGES } from "./AboutTab";
import ContactTab from "./ContactTab";
import Copyright from "../Copyright/Copyright";

// The About language picked during this visit (VI unless changed)
const LANGUAGE_KEY = "about-language";
const savedLanguage = () => {
  try {
    return sessionStorage.getItem(LANGUAGE_KEY) || "vi";
  } catch {
    return "vi";
  }
};

const TABS = [
  { key: "about", label: "About" },
  { key: "contact", label: "Contact" },
];

/*
 * Full-screen panel over the site (which shows through blurred) with two
 * tabs, About and Contact, so visitors can switch without closing it.
 * `tab` is the open tab ("about" / "contact"), or null when closed.
 * Each tab has its own address (/about, /contact; see Music.js).
 * Closes with the × button, Escape, or a click on the empty background;
 * while open, the page behind doesn't scroll.
 */
function InfoPanel({ tab, onTab, onClose }) {
  const closeRef = useRef(null);
  const tabRefs = useRef({});
  const [language, setLanguageState] = useState(savedLanguage); // About: Vietnamese first
  const setLanguage = (next) => {
    setLanguageState(next);
    try {
      sessionStorage.setItem(LANGUAGE_KEY, next); // kept for this visit
    } catch {
      // Storage unavailable: just not remembered
    }
  };
  const open = tab !== null;
  useOverlay(open, onClose, closeRef);

  if (!open) return null;

  // Left / right arrow keys move between the tabs
  const onTabKey = (event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    const index = TABS.findIndex(({ key }) => key === tab);
    const next = TABS[(index + (event.key === "ArrowRight" ? 1 : -1) + TABS.length) % TABS.length].key;
    onTab(next);
    tabRefs.current[next]?.focus();
  };

  return createPortal(
    <div
      className="contact-page"
      role="dialog"
      aria-modal="true"
      aria-label="thangcao"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <button ref={closeRef} className="contact-close" onClick={onClose} aria-label="Close">
        <FiX />
      </button>

      <div className="contact-content">
        <div className="info-head">
          <div className="info-tabs" role="tablist" aria-label="thangcao" onKeyDown={onTabKey}>
            {TABS.map(({ key, label }) => (
              <button
                key={key}
                ref={(element) => (tabRefs.current[key] = element)}
                id={`info-tab-${key}`}
                className={`info-tab ${tab === key ? "is-current" : ""}`}
                role="tab"
                aria-selected={tab === key}
                aria-controls="info-tabpanel"
                tabIndex={tab === key ? 0 : -1}
                onClick={() => onTab(key)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Language switch, for the About text only */}
          {tab === "about" ? (
            <div className="about-languages" role="group" aria-label="Language">
              {LANGUAGES.map(({ key, label }) => (
                <button
                  key={key}
                  className={`about-language ${language === key ? "is-current" : ""}`}
                  onClick={() => setLanguage(key)}
                  aria-pressed={language === key}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {/* key: replays the fade-in when switching tabs */}
        <div
          key={tab}
          id="info-tabpanel"
          className="info-tabpanel"
          role="tabpanel"
          aria-labelledby={`info-tab-${tab}`}
        >
          {tab === "about" ? <AboutTab language={language} /> : <ContactTab />}
        </div>

        {/* Same copyright line as the bottom of the home page */}
        <Copyright className="info-copyright" />
      </div>
    </div>,
    document.body
  );
}

export default InfoPanel;
